import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  PenTool, 
  ChevronLeft,
  BookOpen,
  Lightbulb,
  Target,
  Layers,
  Play
} from "lucide-react";
import { listTexts, getSampleTexts } from "../lib/api";

const GRAMMAR_ACTIVITIES = [
  {
    id: "nature",
    name: "Nature des mots",
    description: "Nom, verbe, adjectif...",
    icon: Lightbulb,
    color: "purple",
    bgColor: "bg-purple-500",
    lightBg: "bg-purple-50",
  },
  {
    id: "fonction",
    name: "Fonctions",
    description: "Sujet, COD, COI...",
    icon: Target,
    color: "indigo",
    bgColor: "bg-indigo-500",
    lightBg: "bg-indigo-50",
  },
  {
    id: "structure",
    name: "Structure",
    description: "GNS, GV, complements...",
    icon: Layers,
    color: "violet",
    bgColor: "bg-violet-500",
    lightBg: "bg-violet-50",
  },
];

export default function GrammaireDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [texts, setTexts] = useState([]);
  const [sampleTexts, setSampleTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);

  const isTeacher = user?.role === "teacher";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [textsRes, samplesRes] = await Promise.all([
        listTexts(),
        getSampleTexts()
      ]);
      setTexts(textsRes.data);
      setSampleTexts(samplesRes.data);
    } catch (err) {
      console.error("Failed to load data:", err);
    }
    setLoading(false);
  };

  const handleStartActivity = (text, activityType) => {
    // Navigate to grammar page with text and activity type
    navigate(`/grammar?text=${encodeURIComponent(text)}&activity=${activityType || "nature"}`);
  };

  return (
    <div 
      data-testid="grammaire-dashboard"
      className="min-h-screen bg-gradient-to-br from-purple-50 to-white"
    >
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg border-b border-purple-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-home-btn"
              onClick={() => navigate("/home")}
              className="p-2 hover:bg-purple-100 rounded-full transition-colors"
            >
              <ChevronLeft className="text-purple-600" size={24} />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center shadow-[0_4px_0_rgb(126,34,206)]">
                <PenTool className="text-white" size={24} />
              </div>
              <div>
                <h1 className="font-fredoka text-2xl font-bold text-purple-700">
                  Grammaire
                </h1>
                <p className="text-sm text-slate-500 font-nunito">
                  Comprendre les phrases
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Activity Types */}
        <section className="mb-10">
          <h2 className="font-fredoka text-xl font-bold text-slate-700 mb-4">
            Type d'exercice
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {GRAMMAR_ACTIVITIES.map((activity) => {
              const Icon = activity.icon;
              const isSelected = selectedActivity === activity.id;
              
              return (
                <button
                  key={activity.id}
                  data-testid={`activity-${activity.id}`}
                  onClick={() => setSelectedActivity(isSelected ? null : activity.id)}
                  className={`
                    flex items-center gap-4 p-5 rounded-2xl border-2 transition-all
                    ${isSelected 
                      ? `${activity.lightBg} border-${activity.color}-400 shadow-lg` 
                      : "bg-white border-slate-100 hover:border-slate-200"
                    }
                  `}
                >
                  <div className={`w-14 h-14 ${activity.bgColor} rounded-xl flex items-center justify-center`}>
                    <Icon className="text-white" size={28} />
                  </div>
                  <div className="text-left">
                    <h3 className="font-fredoka font-bold text-lg text-slate-800">
                      {activity.name}
                    </h3>
                    <p className="text-slate-500 text-sm">
                      {activity.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Text Selection */}
        <section className="mb-10">
          <h2 className="font-fredoka text-xl font-bold text-slate-700 mb-4 flex items-center gap-2">
            <BookOpen className="text-purple-500" size={24} />
            Choisir un texte
          </h2>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-10 h-10 border-4 border-purple-200 border-t-purple-500 rounded-full mx-auto"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Sample texts */}
              {sampleTexts.map((sample, index) => (
                <div
                  key={`sample-${index}`}
                  className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-100 rounded-2xl p-5 hover:border-purple-300 hover:shadow-lg transition-all"
                >
                  <h3 className="font-fredoka font-bold text-lg text-slate-800 mb-2">
                    {sample.title}
                  </h3>
                  <p className="text-slate-500 text-sm line-clamp-2 mb-4">
                    {sample.text?.substring(0, 100)}...
                  </p>
                  
                  <button
                    data-testid={`grammar-sample-${index}`}
                    onClick={() => handleStartActivity(sample.text, selectedActivity)}
                    className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-full font-bold text-sm transition-colors"
                  >
                    <Play size={16} />
                    Analyser
                  </button>
                </div>
              ))}

              {/* User texts */}
              {texts.map((text) => (
                <div
                  key={text.id}
                  className="bg-white border-2 border-slate-100 rounded-2xl p-5 hover:border-purple-200 hover:shadow-lg transition-all"
                >
                  <h3 className="font-fredoka font-bold text-lg text-slate-800 mb-2">
                    {text.title}
                  </h3>
                  <p className="text-slate-500 text-sm line-clamp-2 mb-4">
                    {text.raw_text?.substring(0, 100)}...
                  </p>
                  
                  <button
                    data-testid={`grammar-text-${text.id}`}
                    onClick={() => handleStartActivity(text.raw_text, selectedActivity)}
                    className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-full font-bold text-sm transition-colors"
                  >
                    <Play size={16} />
                    Analyser
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Quick Entry */}
        <section className="bg-white rounded-3xl border-2 border-slate-100 p-6">
          <h2 className="font-fredoka text-xl font-bold text-slate-700 mb-4">
            Ou ecris ta phrase
          </h2>
          <QuickGrammarEntry 
            onAnalyze={(text) => handleStartActivity(text, selectedActivity)} 
          />
        </section>
      </main>
    </div>
  );
}

function QuickGrammarEntry({ onAnalyze }) {
  const [text, setText] = useState("");

  return (
    <div className="flex flex-col md:flex-row gap-4">
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Le chat mange la souris..."
        className="flex-1 h-14 bg-slate-50 border-2 border-slate-200 rounded-xl px-5 text-lg font-nunito focus:border-purple-400 focus:ring-4 focus:ring-purple-100 transition-all outline-none"
        data-testid="quick-grammar-input"
      />
      <button
        onClick={() => text.trim() && onAnalyze(text)}
        disabled={!text.trim()}
        className={`
          px-6 py-3 rounded-full font-fredoka font-bold transition-all
          ${text.trim() 
            ? "bg-purple-500 hover:bg-purple-600 text-white shadow-[0_4px_0_rgb(126,34,206)] active:shadow-none active:translate-y-1" 
            : "bg-slate-200 text-slate-400 cursor-not-allowed"
          }
        `}
        data-testid="quick-grammar-btn"
      >
        Analyser
      </button>
    </div>
  );
}
